"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.firstNonUpscalingTargetReason = exports.allNonUpscalingTargetReasons = exports.targetLargerThanInputWatchedFieldDefUuids = exports.eachUnsatisfiedRequiredGroup = exports.eachVisibleLeafField = exports.isFieldRequired = exports.isFieldVisibleAndEnabled = exports.getLogicOnFieldsFromPrivateField = exports.getLogicOnFieldsFromField = exports.extractFieldsByFieldType = void 0;
const lodash_1 = require("lodash");
const targetResolution_1 = require("./targetResolution");
const walk_1 = require("./runnitFields/walk");
// Local so this module does not depend on the enums barrel for one string.
const RUNNIT_NODE_FIELD_TYPE_SINGLE_SELECT = 'SINGLE_SELECT';
const extractFieldsByFieldType = (fields, fieldType) => (0, walk_1.eachLeafField)(fields).filter((field) => field.type === fieldType);
exports.extractFieldsByFieldType = extractFieldsByFieldType;
const matchesOnValue = (onValue, val) => ((onValue === true && !!val) ||
    ((0, lodash_1.isArray)(onValue) && onValue.some((v) => v === val)));
const getLogicOnFieldsFromField = (field, inputValues, 
// How to turn the WATCHED field's value into { width, height }. Defaults to
// reading a Runnit input value; the Generate API passes its own because its
// media arrives as inputMedia refs, not as a Runnit input value.
resolveInputMediaDimensions) => {
    const result = {
        allDisables: [],
        allShows: [],
        allRequires: [],
        triggeredDisables: [],
        triggeredShows: [],
        triggeredRequires: [],
    };
    const logicFieldDefUuids = (0, lodash_1.keys)((0, lodash_1.get)(field, 'logic.field') || {});
    logicFieldDefUuids.forEach((fieldDefUuid) => {
        if (!field.logic)
            return;
        const val = (inputValues || {})[fieldDefUuid] || null;
        // `require` is a CJS global; alias to avoid shadowing in this shared lib.
        const { disable, show, autoSet, require: requireRule, enforceTargetLargerThanInput, } = field.logic.field[fieldDefUuid];
        if (disable)
            result.allDisables.push(disable);
        if (show)
            result.allShows.push(show);
        if (requireRule)
            result.allRequires.push(requireRule);
        if (disable && matchesOnValue(disable.onValue, val)) {
            result.triggeredDisables.push(disable);
        }
        if (show && matchesOnValue(show.onValue, val)) {
            result.triggeredShows.push(show);
        }
        if (requireRule && matchesOnValue(requireRule.onValue, val)) {
            result.triggeredRequires.push(requireRule);
        }
        // Handle enforceTargetLargerThanInput logic. `fieldDefUuid` is the watched
        // INPUT MEDIA field; `field` is the SINGLE_SELECT carrying the rule, so the
        // comparison reads one value from each.
        if (enforceTargetLargerThanInput) {
            const inputDimensions = resolveInputMediaDimensions
                ? resolveInputMediaDimensions(fieldDefUuid, val)
                : (0, targetResolution_1.extractInputMediaDimensions)(val);
            // Guarded so the common case (no media picked yet) allocates nothing.
            if (inputDimensions) {
                const reason = (0, targetResolution_1.evaluateTargetLargerThanInput)({
                    inputDimensions,
                    selectedValue: (inputValues || {})[field.fieldDefUuid],
                    optionValues: (0, targetResolution_1.targetResolutionOptionValues)(field.display),
                    fieldLabel: field.label,
                    message: enforceTargetLargerThanInput.message,
                });
                if (reason)
                    result.targetLargerThanInputError = reason;
            }
        }
        // Handle autoSet logic
        if (autoSet) {
            // If onValue is provided, gate autoSet execution behind this condition
            let shouldRunAutoSet = true;
            if (!(0, lodash_1.isNil)(autoSet.onValue)) {
                shouldRunAutoSet = !!((autoSet.onValue === true && val) ||
                    ((0, lodash_1.isArray)(autoSet.onValue) && autoSet.onValue.some((v) => v === val)));
            }
            else {
                shouldRunAutoSet = true;
            }
            if (!shouldRunAutoSet) {
                return;
            }
            // Set the visibility and edit properties
            result.autoSetAllowEdit = autoSet.allowEdit || false;
            result.autoSetVisible = autoSet.visible || autoSet.allowEdit || false;
            const dataInputValue = inputValues[fieldDefUuid];
            if (dataInputValue) {
                let extractedValue = null;
                if (autoSet.dataFieldKey) {
                    // Handle image data
                    if ((0, lodash_1.get)(dataInputValue, 'image.nodeRunResult')) {
                        extractedValue = (0, lodash_1.get)(dataInputValue, `image.nodeRunResult.file.${autoSet.dataFieldKey}`);
                    }
                    else if (dataInputValue.nodeRunResult) {
                        extractedValue = (0, lodash_1.get)(dataInputValue, `nodeRunResult.file.${autoSet.dataFieldKey}`);
                    }
                    else if ((0, lodash_1.get)(dataInputValue, 'image.upload')) {
                        extractedValue = (0, lodash_1.get)(dataInputValue, `image.upload.${autoSet.dataFieldKey}`);
                    }
                    else if (dataInputValue.upload) {
                        extractedValue = (0, lodash_1.get)(dataInputValue, `upload.${autoSet.dataFieldKey}`);
                    }
                    // Handle video data
                    if ((0, lodash_1.get)(dataInputValue, 'video.nodeRunResult')) {
                        extractedValue = (0, lodash_1.get)(dataInputValue, `video.nodeRunResult.file.${autoSet.dataFieldKey}`);
                    }
                    else if ((0, lodash_1.get)(dataInputValue, 'video.upload')) {
                        extractedValue = (0, lodash_1.get)(dataInputValue, `video.upload.${autoSet.dataFieldKey}`);
                    }
                    // Fallback to direct property access
                    if (extractedValue === null || extractedValue === undefined) {
                        extractedValue = (0, lodash_1.get)(dataInputValue, autoSet.dataFieldKey);
                    }
                }
                else {
                    extractedValue = dataInputValue;
                }
                if (extractedValue !== null && extractedValue !== undefined) {
                    result.autoSetValue = (autoSet.setToValue !== undefined) ? autoSet.setToValue : extractedValue;
                }
                else {
                    // Only show error if the source field has data but extraction fails
                    // Check if the source field actually has meaningful data
                    const hasImageData = (0, lodash_1.get)(dataInputValue, 'image.nodeRunResult') || (0, lodash_1.get)(dataInputValue, 'image.upload');
                    const hasVideoData = (0, lodash_1.get)(dataInputValue, 'video.nodeRunResult') || (0, lodash_1.get)(dataInputValue, 'video.upload');
                    const hasDirectData = dataInputValue.upload || dataInputValue.nodeRunResult;
                    if (autoSet.dataFieldKey && (hasImageData || hasVideoData || hasDirectData)) {
                        result.autoSetError = autoSet.errorMessage || `Unable to extract ${autoSet.dataFieldKey} from input data`;
                    }
                    // If no meaningful data exists, don't show error (field is just empty)
                }
            }
            // If dataInputValue is null/undefined, don't show error (field is just empty)
        }
    });
    return result;
};
exports.getLogicOnFieldsFromField = getLogicOnFieldsFromField;
const getLogicOnFieldsFromPrivateField = (field, inputValues) => {
    const result = {
        allShows: [],
        triggeredShows: [],
    };
    const logicFieldDefUuids = (0, lodash_1.keys)((0, lodash_1.get)(field, 'logic.field') || {});
    logicFieldDefUuids.forEach((fieldDefUuid) => {
        if (!(0, lodash_1.get)(field, 'logic.field'))
            return;
        const val = (inputValues || {})[fieldDefUuid] || null;
        const { show, autoSet } = (0, lodash_1.get)(field, `logic.field[${fieldDefUuid}]`);
        if (show) {
            result.allShows.push(show);
            if ((show.onValue === true && val) ||
                ((0, lodash_1.isArray)(show.onValue) && show.onValue.some((v) => v === val))) {
                result.triggeredShows.push(show);
            }
        }
        // Handle autoSet logic
        if (autoSet) {
            // If onValue is provided, gate autoSet execution behind this condition
            let shouldRunAutoSet = true;
            if (!(0, lodash_1.isNil)(autoSet.onValue)) {
                shouldRunAutoSet = !!((autoSet.onValue === true && val) ||
                    ((0, lodash_1.isArray)(autoSet.onValue) && autoSet.onValue.some((v) => v === val)));
            }
            else {
                shouldRunAutoSet = true;
            }
            if (!shouldRunAutoSet) {
                return;
            }
            // Set the visibility and edit properties
            result.autoSetAllowEdit = autoSet.allowEdit || false;
            result.autoSetVisible = autoSet.visible || autoSet.allowEdit || false;
            const dataInputValue = inputValues[fieldDefUuid];
            if (dataInputValue) {
                // Use the same logic as DataFieldMixin to extract the value
                let extractedValue = null;
                if (autoSet.dataFieldKey) {
                    // Handle image data
                    if ((0, lodash_1.get)(dataInputValue, 'image.nodeRunResult')) {
                        extractedValue = (0, lodash_1.get)(dataInputValue, `image.nodeRunResult.file.${autoSet.dataFieldKey}`);
                    }
                    else if (dataInputValue.nodeRunResult) {
                        extractedValue = (0, lodash_1.get)(dataInputValue, `nodeRunResult.file.${autoSet.dataFieldKey}`);
                    }
                    else if ((0, lodash_1.get)(dataInputValue, 'image.upload')) {
                        extractedValue = (0, lodash_1.get)(dataInputValue, `image.upload.${autoSet.dataFieldKey}`);
                    }
                    else if (dataInputValue.upload) {
                        extractedValue = (0, lodash_1.get)(dataInputValue, `upload.${autoSet.dataFieldKey}`);
                    }
                    // Handle video data
                    if ((0, lodash_1.get)(dataInputValue, 'video.nodeRunResult')) {
                        extractedValue = (0, lodash_1.get)(dataInputValue, `video.nodeRunResult.file.${autoSet.dataFieldKey}`);
                    }
                    else if ((0, lodash_1.get)(dataInputValue, 'video.upload')) {
                        extractedValue = (0, lodash_1.get)(dataInputValue, `video.upload.${autoSet.dataFieldKey}`);
                    }
                    // Fallback to direct property access
                    if (extractedValue === null || extractedValue === undefined) {
                        extractedValue = (0, lodash_1.get)(dataInputValue, autoSet.dataFieldKey);
                    }
                }
                else {
                    extractedValue = dataInputValue;
                }
                if (extractedValue !== null && extractedValue !== undefined) {
                    result.autoSetValue = (autoSet.setToValue !== undefined) ? autoSet.setToValue : extractedValue;
                }
                else {
                    // Only show error if the source field has data but extraction fails
                    // Check if the source field actually has meaningful data
                    const hasImageData = (0, lodash_1.get)(dataInputValue, 'image.nodeRunResult') || (0, lodash_1.get)(dataInputValue, 'image.upload');
                    const hasVideoData = (0, lodash_1.get)(dataInputValue, 'video.nodeRunResult') || (0, lodash_1.get)(dataInputValue, 'video.upload');
                    const hasDirectData = dataInputValue.upload || dataInputValue.nodeRunResult;
                    if (autoSet.dataFieldKey && (hasImageData || hasVideoData || hasDirectData)) {
                        result.autoSetError = autoSet.errorMessage || `Unable to extract ${autoSet.dataFieldKey} from input data`;
                    }
                    // If no meaningful data exists, don't show error (field is just empty)
                }
            }
            // If dataInputValue is null/undefined, don't show error (field is just empty)
        }
    });
    return result;
};
exports.getLogicOnFieldsFromPrivateField = getLogicOnFieldsFromPrivateField;
const isFieldVisibleAndEnabled = (field, fieldValues, uiOnly = false // The autoSet can hide a field from the UI, but we still need to check if it's enabled for pricing logic
) => {
    if (!field.logic)
        return true; // No logic means always visible and enabled
    const { allShows, triggeredDisables, triggeredShows, autoSetVisible, autoSetAllowEdit, } = (0, exports.getLogicOnFieldsFromField)(field, fieldValues);
    const isShown = !!(!allShows.length || triggeredShows.length);
    const isDisabled = !!(isShown && triggeredDisables.length);
    // If field has autoset logic and autoSetVisible is explicitly false and not editable, hide the field
    if (!autoSetVisible && !autoSetAllowEdit && uiOnly) {
        return false;
    }
    return isShown && !isDisabled;
};
exports.isFieldVisibleAndEnabled = isFieldVisibleAndEnabled;
const isFieldRequired = (field, fieldValues) => {
    if (field.required)
        return true;
    if (!field.logic)
        return false;
    const { triggeredRequires } = (0, exports.getLogicOnFieldsFromField)(field, fieldValues);
    return !!triggeredRequires.length;
};
exports.isFieldRequired = isFieldRequired;
/**
 * Every leaf field that is visible and enabled, checking visibility at EVERY
 * level: a hidden group prunes its whole subtree, and a hidden leaf inside a
 * visible group is still skipped.
 *
 * Lives here rather than in runnitFields/walk.ts because it needs
 * isFieldVisibleAndEnabled, and ADR-0005 makes that folder a deps-free leaf.
 */
const eachVisibleLeafField = (
// `any[]`: callers pass both the Firestore field tree and, in tests, partial
// structural fixtures. Same latitude the recursions this replaces had.
fields, fieldValues, uiOnly = false) => {
    const out = [];
    for (const entry of (fields || [])) {
        if (!entry)
            continue;
        if (!(0, exports.isFieldVisibleAndEnabled)(entry, fieldValues, uiOnly))
            continue;
        const group = entry;
        if (group.__rgroup) {
            const nested = (0, exports.eachVisibleLeafField)(group.fields, fieldValues, uiOnly);
            for (const leaf of nested)
                out.push(leaf);
            continue;
        }
        const field = entry;
        if (field.__rfield)
            out.push(field);
    }
    return out;
};
exports.eachVisibleLeafField = eachVisibleLeafField;
/**
 * Every visible `required` group whose visible leaf fields all lack a value.
 * Same "has a value" test as a required leaf field, so a group of BOOLEAN
 * toggles is satisfied once any one of them is on.
 */
const eachUnsatisfiedRequiredGroup = (fields, fieldValues) => {
    const out = [];
    for (const entry of (fields || [])) {
        if (!entry)
            continue;
        const group = entry;
        if (!group.__rgroup || !(0, exports.isFieldVisibleAndEnabled)(entry, fieldValues))
            continue;
        if (group.required && !(0, exports.eachVisibleLeafField)(group.fields, fieldValues).some((field) => fieldValues[field.fieldDefUuid])) {
            out.push(group);
        }
        for (const nested of (0, exports.eachUnsatisfiedRequiredGroup)(group.fields, fieldValues))
            out.push(nested);
    }
    return out;
};
exports.eachUnsatisfiedRequiredGroup = eachUnsatisfiedRequiredGroup;
/**
 * The fieldDefUuids of the INPUT MEDIA fields that some SINGLE_SELECT watches
 * through `enforceTargetLargerThanInput`, deduplicated, in field order.
 *
 * Pure and in-memory. A caller that must do I/O to resolve dimensions (the
 * Generate API reads Firestore) uses this to load only what the rule actually
 * watches, and to skip the work entirely when the list is empty.
 *
 * Visibility is deliberately NOT considered: this only answers "what does the
 * rule watch". allNonUpscalingTargetReasons applies visibility when it evaluates.
 */
const targetLargerThanInputWatchedFieldDefUuids = (fields) => {
    // An array with indexOf, not an object literal and not a Set. An object would
    // hit the prototype (seen['constructor'] is truthy, silently dropping that
    // uuid); a Set is not a declared global for web's eslint, which lints
    // shared/dist with `env: { node: true }` and no es6 env, so `new Set()` fails
    // the web build with `'Set' is not defined`. The watched list is 1-3 entries.
    const watched = [];
    for (const field of (0, walk_1.eachLeafField)(fields)) {
        const rulesByWatchedUuid = (field.logic && field.logic.field) || {};
        for (const watchedFieldDefUuid of (0, lodash_1.keys)(rulesByWatchedUuid)) {
            const entryRules = rulesByWatchedUuid[watchedFieldDefUuid];
            if (!entryRules || !entryRules.enforceTargetLargerThanInput)
                continue;
            if (watched.indexOf(watchedFieldDefUuid) !== -1)
                continue;
            watched.push(watchedFieldDefUuid);
        }
    }
    return watched;
};
exports.targetLargerThanInputWatchedFieldDefUuids = targetLargerThanInputWatchedFieldDefUuids;
/**
 * Every Non-Upscaling Target in the tree, in field order.
 *
 * Visibility is checked at every level by eachVisibleLeafField, so a field
 * hidden by show/disable logic never blocks a run. The rule itself is
 * interpreted by getLogicOnFieldsFromField rather than re-read here.
 *
 * Never throws. An unresolvable input, an unmapped target, or a missing value
 * all yield no reason for that field.
 */
const allNonUpscalingTargetReasons = (fields, // see eachVisibleLeafField
inputValues, resolveDimensions) => {
    const values = inputValues || {};
    const reasons = [];
    for (const field of (0, exports.eachVisibleLeafField)(fields, values)) {
        if (field.type !== RUNNIT_NODE_FIELD_TYPE_SINGLE_SELECT)
            continue;
        const { targetLargerThanInputError } = (0, exports.getLogicOnFieldsFromField)(field, values, resolveDimensions);
        if (targetLargerThanInputError)
            reasons.push(targetLargerThanInputError);
    }
    return reasons;
};
exports.allNonUpscalingTargetReasons = allNonUpscalingTargetReasons;
/**
 * The first Non-Upscaling Target in the tree, or null to allow.
 *
 * For callers that only need to reject: both servers stop at the first reason
 * because one is enough to refuse the run. The client uses
 * allNonUpscalingTargetReasons so its Run message lists every blocked field the
 * way the sibling validations do.
 */
const firstNonUpscalingTargetReason = (fields, // see eachVisibleLeafField
inputValues, resolveDimensions) => {
    const reasons = (0, exports.allNonUpscalingTargetReasons)(fields, inputValues, resolveDimensions);
    return reasons.length ? reasons[0] : null;
};
exports.firstNonUpscalingTargetReason = firstNonUpscalingTargetReason;
